import pLimit from "p-limit"
import { isBrowser } from "src/utilities/env"
import { ResponseParseOptions, tryParseJson, tryParseJsonOrNull } from "src/utilities/http"

const browserBaseUrl = "/api/rewrites/woocommerce"
const baseUrl = isBrowser ? browserBaseUrl : process.env.WOO_ORIGIN
const apiBasePath = "/wp-json/wc/store/v1"
const limit = pLimit(5)

if (!baseUrl) {
  throw new Error("Please set WOO_ORIGIN")
}

let nonce: string | null = null

async function initNonce() {
  const response = await fetch(`${browserBaseUrl}${apiBasePath}/cart?bust=${Date.now()}`, {
    credentials: "include",
    cache: "no-store",
  })

  nonce = response.headers.get("nonce")
}

export type WcStoreRequestOverrides = Pick<RequestInit, "method" | "headers" | "body"> & {
  relativeUrl: string
}

/**
 * @param relativePath relative to `/wp-json/wc/store/v1`
 */
export async function wcStoreFetchResponse(overrides: WcStoreRequestOverrides, retried = false) {
  const method = overrides.method || "GET"
  const requiresNonce = method !== "GET" || overrides.relativeUrl.includes("checkout")

  if (requiresNonce && !isBrowser) {
    throw new Error("This function can only be used in the browser if used with a POST request")
  }
  if (requiresNonce && !nonce) {
    await initNonce()
  }
  if (requiresNonce && !nonce) {
    throw new Error("No nonce found")
  }

  const cacheBust = (overrides.relativeUrl.includes("?") ? "&" : "?") + "now=" + Date.now()
  const url = `${baseUrl}${apiBasePath}${overrides.relativeUrl}${cacheBust}`
  const options: RequestInit = {
    ...overrides,
    method,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      nonce: nonce || "",
      ...overrides?.headers,
    },
    credentials: "include",
    cache: "no-store",
  } satisfies RequestInit

  const retryText = retried ? " (retry)" : ""
  console.debug(`📡 ${method} ${url}${retryText}`)
  const start = Date.now()

  const response = await limit(() => fetch(url, options))

  const duration = Date.now() - start
  console.debug(`📡 ${method} ${url}${retryText} took ${duration}ms`)

  const newNonce = response.headers.get("nonce")

  if (newNonce) {
    nonce = newNonce
  }

  // Retry once on 500 and on 403 (nonce issue) errors:
  if (!response.ok && (response.status >= 500 || response.status === 403) && !retried) {
    return wcStoreFetchResponse(overrides, true)
  }

  return response
}

/**
 * @param relativePath relative to `/wp-json/wc/store/v1`
 */
export async function wcStoreFetchOrThrow<T>(overrides: WcStoreRequestOverrides, parseOptions?: ResponseParseOptions) {
  const response = await wcStoreFetchResponse(overrides)

  return tryParseJson<T>(response, parseOptions)
}

/**
 * @param relativePath relative to `/wp-json/wc/store/v1`
 */
export async function wcStoreFetchOrNull<T>(overrides: WcStoreRequestOverrides, parseOptions?: ResponseParseOptions) {
  const response = await wcStoreFetchResponse(overrides)

  return tryParseJsonOrNull<T>(response, parseOptions)
}
